/**
 * BatchUploadFlow — multi-file and folder upload with auto-classification.
 * Accepts multiple files or entire folders, classifies each via LLM,
 * extracts fields, and shows a consolidated review queue.
 */
import { useState, useRef } from "react";
import { Upload, FolderOpen, FileText, Check, X, Loader2, AlertTriangle, ChevronDown } from "lucide-react";
import { api } from "../../lib/api.ts";
import { DOC_TYPE_FIELD_MAP, detectDocTypeFromFilename as detectDocType } from "../../lib/doc-types.ts";

interface BatchUploadFlowProps {
  projectId: string;
  countryCode: string;
  onComplete?: () => void;
}

interface QueuedFile {
  file: File;
  relativePath: string; // folder path if from directory upload
  status: "pending" | "classifying" | "extracting" | "done" | "skipped" | "error";
  docType: string | null;
  classificationConfidence: number | null;
  fieldsExtracted: number;
  error: string | null;
}

interface BatchResult {
  filesProcessed: number;
  filesSkipped: number;
  fieldsExtracted: number;
  fieldsNeedReview: number;
  errors: string[];
}

const ACCEPTED_EXTENSIONS = new Set([".pdf", ".docx", ".xlsx", ".doc", ".xls", ".png", ".jpg", ".jpeg"]);

function isAcceptedFile(name: string): boolean {
  const ext = name.substring(name.lastIndexOf(".")).toLowerCase();
  return ACCEPTED_EXTENSIONS.has(ext);
}

export function BatchUploadFlow({ projectId, countryCode, onComplete }: BatchUploadFlowProps) {
  const [queue, setQueue] = useState<QueuedFile[]>([]);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<BatchResult | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);

  function addFiles(fileList: FileList) {
    const newFiles: QueuedFile[] = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i]!;
      if (!isAcceptedFile(file.name)) continue;
      if (file.size > 20 * 1024 * 1024) continue; // skip >20MB

      // Get relative path for folder uploads
      const relativePath = (file as any).webkitRelativePath || file.name;

      newFiles.push({
        file,
        relativePath,
        status: "pending",
        docType: null,
        classificationConfidence: null,
        fieldsExtracted: 0,
        error: null,
      });
    }
    setQueue(prev => [...prev, ...newFiles]);
  }

  async function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    // Use webkitGetAsEntry for folder drops (plain files fallback to dataTransfer.files)
    const items = e.dataTransfer.items;
    if (items?.length > 0 && items[0]?.webkitGetAsEntry) {
      const files: File[] = [];
      const entries: FileSystemEntry[] = [];
      for (let i = 0; i < items.length; i++) {
        const entry = items[i]!.webkitGetAsEntry();
        if (entry) entries.push(entry);
      }
      async function readEntry(entry: FileSystemEntry, path: string) {
        if (entry.isFile) {
          const file = await new Promise<File>((resolve) => (entry as FileSystemFileEntry).file(resolve));
          // Preserve relative path for folder structure
          Object.defineProperty(file, "webkitRelativePath", { value: path + file.name, writable: false });
          files.push(file);
        } else if (entry.isDirectory) {
          const reader = (entry as FileSystemDirectoryEntry).createReader();
          const subEntries = await new Promise<FileSystemEntry[]>((resolve) => reader.readEntries(resolve));
          for (const sub of subEntries) {
            await readEntry(sub, path + entry.name + "/");
          }
        }
      }
      for (const entry of entries) await readEntry(entry, "");
      if (files.length > 0) {
        const dt = new DataTransfer();
        files.forEach(f => dt.items.add(f));
        addFiles(dt.files);
        return;
      }
    }
    // Fallback for simple file drops
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  }

  function removeFile(index: number) {
    setQueue(prev => prev.filter((_, i) => i !== index));
  }

  async function processQueue() {
    setProcessing(true);
    const totals: BatchResult = { filesProcessed: 0, filesSkipped: 0, fieldsExtracted: 0, fieldsNeedReview: 0, errors: [] };

    for (let i = 0; i < queue.length; i++) {
      const item = queue[i]!;
      if (item.status !== "pending") continue;

      // Phase 1: Classify
      updateFile(i, { status: "classifying" });

      try {
        // Upload the file first
        const doc = await api.uploadDocument(projectId, item.file, "");

        // Try LLM classification via the cloud classify endpoint
        let docType: string | null = null;
        try {
          const classResult = await api.classifyDocument(projectId, doc.id);
          docType = classResult.docType;
          updateFile(i, { docType, classificationConfidence: classResult.confidence });
        } catch {
          // Fallback: try filename-based detection
          docType = detectDocTypeFromFilename(item.file.name);
          updateFile(i, { docType, classificationConfidence: docType ? 0.5 : 0 });
        }

        if (!docType) {
          updateFile(i, { status: "skipped", error: "Could not determine document type" });
          totals.filesSkipped++;
          continue;
        }

        // Update the source_document with the correct doc_type
        await api.updateDocumentType(projectId, doc.id, docType);

        // Phase 2: Extract
        updateFile(i, { status: "extracting" });
        const extraction = await api.extractDocument(projectId, doc.id);

        // Phase 3: Auto-commit high-confidence fields
        const autoUpdates = extraction.classified.autoCommitted.map((r: any) => ({
          field_id: r.field_id,
          value: r.value,
          source_document_id: doc.id,
          source_page: r.source_page,
        }));

        if (autoUpdates.length > 0) {
          await api.batchUpdateFields(projectId, autoUpdates);
        }

        const reviewCount = extraction.classified.needsReview.length + extraction.classified.alwaysConfirm.length;
        updateFile(i, {
          status: "done",
          fieldsExtracted: extraction.results.length,
        });
        totals.filesProcessed++;
        totals.fieldsExtracted += autoUpdates.length;
        totals.fieldsNeedReview += reviewCount;

      } catch (err: any) {
        updateFile(i, { status: "error", error: err.message || "Processing failed" });
        totals.errors.push(`${item.file.name}: ${err.message}`);
      }
    }

    setResult(totals);
    setProcessing(false);
    onComplete?.();
  }

  function updateFile(index: number, update: Partial<QueuedFile>) {
    setQueue(prev => prev.map((f, i) => i === index ? { ...f, ...update } : f));
  }

  // Shared with DocumentUploadFlow — see detectDocTypeFromFilename in lib/doc-types.ts. It used
  // to be copy-pasted into both, and the copy here returned the first type whose *description*
  // contained any word from the filename, which let "community" in the tripartite agreement's
  // description swallow every community-minutes upload.
  const detectDocTypeFromFilename = (name: string) => detectDocType(name, countryCode);

  const pendingCount = queue.filter(f => f.status === "pending").length;
  const doneCount = queue.filter(f => f.status === "done").length;
  const errorCount = queue.filter(f => f.status === "error").length;

  // ─── Render ───

  // No files queued — show dropzone
  if (queue.length === 0 && !result) {
    return (
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { setDragOver(false); handleDrop(e); }}
        className={`border-2 border-dashed rounded-2xl text-center transition-colors ${
          dragOver ? "border-brand bg-brand-light" : "border-border"
        }`}
      >
        <div className="flex items-center justify-center gap-4 px-4 py-4">
          <button
            onClick={() => fileRef.current?.click()}
            className="flex-1 flex flex-col items-center gap-1.5 px-4 py-4 rounded-2xl hover:bg-surface-tertiary transition-colors cursor-pointer"
          >
            <Upload size={20} className="text-text-tertiary" />
            <span className="text-[14px] font-medium">Select files</span>
            <span className="text-[12px] text-text-tertiary">PDF, DOCX, XLSX, images</span>
          </button>
          <div className="text-text-tertiary text-[13px] flex-shrink-0">or</div>
          <button
            onClick={() => folderRef.current?.click()}
            className="flex-1 flex flex-col items-center gap-1.5 px-4 py-4 rounded-2xl hover:bg-surface-tertiary transition-colors cursor-pointer"
          >
            <FolderOpen size={20} className="text-text-tertiary" />
            <span className="text-[14px] font-medium">Select folder</span>
            <span className="text-[12px] text-text-tertiary">Includes subfolders</span>
          </button>
        </div>
        <p className="text-[12px] text-text-tertiary pb-3">
          Drop files or folders anywhere here. Each file will be auto-classified and extracted.
        </p>

        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.docx,.xlsx,.doc,.xls,.png,.jpg,.jpeg"
          multiple
          className="hidden"
          onChange={e => e.target.files && addFiles(e.target.files)}
        />
        <input
          ref={folderRef}
          type="file"
          {...{ webkitdirectory: "", directory: "" } as any}
          className="hidden"
          onChange={e => e.target.files && addFiles(e.target.files)}
        />
      </div>
    );
  }

  // Results
  if (result && !processing) {
    return (
      <div className="bg-surface border border-border rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Check size={18} className="text-brand" />
          <span className="text-[17px] font-medium">Batch upload complete</span>
        </div>

        <div className="grid grid-cols-4 gap-3 text-center text-[13px] mb-4">
          <div className="bg-brand-light rounded-lg px-2 py-2">
            <div className="text-[18px] font-medium text-brand">{result.filesProcessed}</div>
            <div className="text-text-tertiary">Processed</div>
          </div>
          <div className="bg-surface-secondary rounded-lg px-2 py-2">
            <div className="text-[18px] font-medium">{result.fieldsExtracted}</div>
            <div className="text-text-tertiary">Fields extracted</div>
          </div>
          <div className="bg-warning-light rounded-lg px-2 py-2">
            <div className="text-[18px] font-medium text-warning-text">{result.fieldsNeedReview}</div>
            <div className="text-text-tertiary">Need review</div>
          </div>
          <div className="bg-surface-secondary rounded-lg px-2 py-2">
            <div className="text-[18px] font-medium">{result.filesSkipped}</div>
            <div className="text-text-tertiary">Skipped</div>
          </div>
        </div>

        {result.errors.length > 0 && (
          <div className="bg-danger-light rounded-lg px-3 py-2 text-[13px] text-danger mb-3">
            {result.errors.length} error{result.errors.length > 1 ? "s" : ""}: {result.errors[0]}
            {result.errors.length > 1 && ` (+${result.errors.length - 1} more)`}
          </div>
        )}

        <button
          onClick={() => { setQueue([]); setResult(null); }}
          className="text-[14px] text-text-secondary hover:text-text-primary"
        >
          Upload more files
        </button>
      </div>
    );
  }

  // Queue view — files staged for processing
  return (
    <div className="bg-surface border border-border rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[15px] font-medium">
          {processing ? `Processing ${doneCount + errorCount}/${queue.length}...` : `${queue.length} file${queue.length > 1 ? "s" : ""} ready`}
        </span>
        <div className="flex items-center gap-2">
          {!processing && (
            <button
              onClick={() => { fileRef.current?.click(); }}
              className="text-[13px] text-text-secondary hover:text-text-primary"
            >
              + Add more
            </button>
          )}
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-text-tertiary"
          >
            <ChevronDown size={14} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {processing && (
        <div className="h-1.5 rounded-full bg-surface-tertiary overflow-hidden mb-3">
          <div
            className="h-full bg-brand rounded-full transition-all duration-500"
            style={{ width: `${((doneCount + errorCount) / queue.length) * 100}%` }}
          />
        </div>
      )}

      {/* File list (collapsible) */}
      {expanded && (
        <div className="space-y-1 max-h-48 overflow-y-auto mb-3">
          {queue.map((item, i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-[13px]">
              {item.status === "pending" && <FileText size={14} className="text-text-tertiary flex-shrink-0" />}
              {item.status === "classifying" && <Loader2 size={14} className="text-info animate-spin flex-shrink-0" />}
              {item.status === "extracting" && <Loader2 size={14} className="text-brand animate-spin flex-shrink-0" />}
              {item.status === "done" && <Check size={14} className="text-brand flex-shrink-0" />}
              {item.status === "skipped" && <X size={14} className="text-text-tertiary flex-shrink-0" />}
              {item.status === "error" && <AlertTriangle size={14} className="text-danger flex-shrink-0" />}

              <span className="flex-1 truncate" title={item.relativePath}>
                {item.relativePath}
              </span>

              {item.docType && (
                <span className="px-1.5 py-0.5 bg-surface-tertiary rounded text-[11px] text-text-tertiary flex-shrink-0">
                  {item.docType.replace(/_/g, " ")}
                </span>
              )}

              {item.fieldsExtracted > 0 && (
                <span className="text-[11px] text-brand flex-shrink-0">{item.fieldsExtracted} fields</span>
              )}

              {item.error && (
                <span className="text-[11px] text-danger flex-shrink-0 truncate max-w-[120px]" title={item.error}>{item.error}</span>
              )}

              {!processing && item.status === "pending" && (
                <button onClick={() => removeFile(i)} className="text-text-tertiary hover:text-danger flex-shrink-0">
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      {!processing && (
        <div className="flex items-center justify-between pt-3 border-t border-border">
          <button
            onClick={() => { setQueue([]); setResult(null); }}
            className="text-[13px] text-text-secondary"
          >
            Clear all
          </button>
          <button
            onClick={processQueue}
            disabled={pendingCount === 0}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-brand text-on-solid rounded-xl text-[14px] font-medium disabled:opacity-40"
          >
            <Upload size={14} />
            Process {pendingCount} file{pendingCount > 1 ? "s" : ""}
          </button>
        </div>
      )}

      {/* Hidden inputs for add-more */}
      <input
        ref={fileRef}
        type="file"
        accept=".pdf,.docx,.xlsx,.doc,.xls,.png,.jpg,.jpeg"
        multiple
        className="hidden"
        onChange={e => { if (e.target.files) addFiles(e.target.files); e.target.value = ""; }}
      />
      <input
        ref={folderRef}
        type="file"
        {...{ webkitdirectory: "", directory: "" } as any}
        className="hidden"
        onChange={e => { if (e.target.files) addFiles(e.target.files); e.target.value = ""; }}
      />
    </div>
  );
}
